import React, { MutableRefObject } from 'react';
import { I18n } from 'aws-amplify';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Button, createStyles, makeStyles } from '@material-ui/core';
import StringUtils from '../../../utils/StringUtils';
import {
  OnboardingFormProps,
  OnboardingFormValues,
} from './onboardingFormTypes';
import OnboardingCard from '../OnboardingCard';
import OnboardingFormLayout from './OnboardingFormLayout';
import { OnboardingButtonContainer } from './OnboardingButtonContainer';
import { BaseTextField } from '../../TextField';
import { TrackEvent } from '../../../utils/AnalyticsUtils';
import { RouteContext } from '../../../context';
import { OnboardingLoginFormFooterActions } from './OboardingLoginFormFooterActions';
import OnboardingButton from '../OnboardingButton';
import { OnboardingTrackEventName } from '../../../constants/onboardingConsts';
import BaseTypography from '../../Text/BaseTypography';
import { AUTH_STATES } from '../../../constants/authConsts';
import { GraySmall } from '../../../theme/colors';
import { GetPasswordValidation } from '../../../utils/AuthUtils';

export interface OnboardingLoginProps {
  showOnboardingLoginActions?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    passwordInputLabel: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  }),
);

/**
 * Onboarding login challenge form. This form is used to start the login process for
 * an internal user. This is used in the following scenarios:
 * 1. portal login flow: enter email and password
 * 2. admin login flow: enter email and go through the custom login flow with pass code
 * 3. create portal (onboarding) flow: enter email and password which will either
 * be a new user or existing user. Existing user will have their info prefilled in the
 * create portal flow.
 */
export const OnboardingLoginForm: React.FC<OnboardingFormProps &
  OnboardingLoginProps> = React.forwardRef(
  (
    {
      initialValues,
      handleSubmitDone,
      showOnboardingLoginActions = false,
      trackedEvents,
      onAnalyticsEventTracked,
    },
    ref,
  ) => {
    const classes = useStyles();
    const { pathname } = React.useContext(RouteContext);
    const isAdminLogin = pathname === '/admin'; // admin login flow
    const isPortalLogin = pathname === '/login' || pathname === '/add-account'; // portal login flow

    const schema = Yup.object()
      .strict(true)
      .shape({
        email: Yup.string()
          .email('Invalid email')
          .required('Email is required'),
        password: GetPasswordValidation({
          isPortalLogin,
          isAdminLogin,
        }),
      });

    const onSubmit = async (values: OnboardingFormValues) => {
      const parsedValues = {
        ...values,
        email:
          // for backward compatibility do not lowercase email if login
          // only in onboarding to create new portal lowercase email
          !isPortalLogin
            ? StringUtils.RemoveSpaceToLowerCase(values.email)
            : StringUtils.RemoveSpaceFromString(values.email), // update email to remove space and lowercase
      };

      const isSuccess = await handleSubmitDone(parsedValues);

      if (isSuccess && !trackedEvents[OnboardingTrackEventName.Lead]) {
        TrackEvent('Onboarding - Portal Account Created', {
          email: parsedValues.email,
        });
        onAnalyticsEventTracked(OnboardingTrackEventName.Lead);
      }
    };

    const renderPasswordInputLabel = () =>
      !isPortalLogin ? I18n.get('Set a password') : '';

    return (
      <>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={onSubmit}
          innerRef={ref as MutableRefObject<FormikProps<OnboardingFormValues>>}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            isSubmitting,
          }) => (
            <form noValidate onSubmit={handleSubmit} data-testid="testingform">
              <OnboardingFormLayout>
                <OnboardingCard>
                  <BaseTextField
                    InputProps={{
                      'data-testid': 'emailInput',
                    }}
                    sizeVariant="tall"
                    id="login-username"
                    autoFocus={!initialValues.email}
                    fullWidth
                    type="text"
                    key="email"
                    name="email"
                    variant="outlined"
                    onBlur={handleBlur}
                    onInput={handleChange}
                    value={
                      values.email
                        ? StringUtils.RemoveSpaceFromString(values.email)
                        : ''
                    }
                    label={I18n.get('Work email')}
                    error={Boolean(touched.email && errors.email)}
                    helperText={(touched.email && errors.email) || ' '}
                    autoComplete="off"
                  />

                  {!isAdminLogin && (
                    <div>
                      {!renderPasswordInputLabel() && (
                        <div className={classes.passwordInputLabel}>
                          <BaseTypography fontType="13Medium">
                            {I18n.get('Password')}
                          </BaseTypography>
                          <Button
                            href={`?step=${AUTH_STATES.FORGOT_PASSWORD}`}
                            tabIndex="-1" // should not be focusable via sequential keyboard navigation.
                          >
                            <BaseTypography
                              fontType="13Regular"
                              style={{ color: GraySmall }}
                            >
                              Forgot password?
                            </BaseTypography>
                          </Button>
                        </div>
                      )}
                      <BaseTextField
                        sizeVariant="tall"
                        id="login-password"
                        fullWidth
                        type="password"
                        InputProps={{
                          'data-testid': 'passwordInput',
                        }}
                        key="password"
                        name="password"
                        variant="outlined"
                        onBlur={handleBlur}
                        onInput={handleChange}
                        value={values.password}
                        label={renderPasswordInputLabel()}
                        error={Boolean(touched.password && errors.password)}
                        helperText={(touched.password && errors.password) || ''}
                        autoComplete="off"
                        autoFocus={Boolean(initialValues.email)}
                      />
                    </div>
                  )}
                  <OnboardingButtonContainer>
                    <OnboardingButton
                      type="submit"
                      isLoading={isSubmitting}
                      data-testid="submit-button"
                      color="primary"
                      variant="contained"
                      fullWidth
                      htmlId="create-account-submit-button"
                    >
                      {isPortalLogin || isAdminLogin
                        ? 'Sign in'
                        : 'Create an account'}
                    </OnboardingButton>
                  </OnboardingButtonContainer>
                </OnboardingCard>
              </OnboardingFormLayout>
            </form>
          )}
        </Formik>
        <OnboardingLoginFormFooterActions
          showOnboardingLoginActions={showOnboardingLoginActions}
        />
      </>
    );
  },
);
